<template>
    <b-button
        v-b-tooltip.hover
        size="lg"
        class="custom-icon-button"
        variant="icon"
        :title="$translate('export_to_csv')"
        @click="exportToCSV"
    >
        <csv-icon />
    </b-button>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { ALL_ISTANBUL_EVENTS, ALL_MK_EVENTS, TREATMENT_PAGE_VIEWS } from '../../../business/const'
import UIUtils from '../../../business/utils/UIUtils'
import Utils from '../../../business/utils/Utils'
import CsvIcon from './icons/CsvIcon'

export default {
    name: 'CsvButton',
    components: { CsvIcon },
    computed: {
        ...mapState({
            treatment: state => state.timelapse.selectedTreatment,
            patient: state => state.timelapse.selectedPatient,
            currentView: state => state.navigation.currentView
        }),
        ...mapGetters({
            isFdaFriendly: 'common/isFdaFriendly',
            getPnText: 'timelapse/getPnText',
            getHazards: 'timelapse/getHazards',
            getClinicGardnerLabel: 'timelapse/getClinicGardnerLabel'
        })
    },
    methods: {
        exportToCSV() {
            this.$log.actionEvent('export_to_csv')

            const fieldsMap = {
                e_id: this.$translate('e_id'),
                well: this.$translate('well'),
                inst_slide: this.$translate('inst_slide'),
                status: this.$translate('status'),
                oocyteAge: this.$translate('oocyte_age'),
                patientAge: this.$translate('patient_age'),
                pgta: this.$translate('pgta'),
                gardner: this.$translate('gardner')
            }

            if (!this.isFdaFriendly) {
                fieldsMap.rank = this.$translate('rank')
                fieldsMap.implantation = this.$translate('eq_score')
                fieldsMap.blast = this.$translate('blast')
                fieldsMap.pnCount = this.$translate('pn_count')
                fieldsMap.abnor = this.$translate('abnor')

                if (this.currentView === TREATMENT_PAGE_VIEWS.PERFORMANCE_TABLE) {
                    for (const mkEventSN of ALL_MK_EVENTS) {
                        fieldsMap[mkEventSN] = this.$translate(mkEventSN)
                    }
                } else if (this.currentView === TREATMENT_PAGE_VIEWS.ISTANBUL_TABLE) {
                    for (const istanbulEvent of ALL_ISTANBUL_EVENTS) {
                        fieldsMap[istanbulEvent.label] = this.$translate(istanbulEvent.label)
                    }
                }
            }

            const fields = Object.values(fieldsMap)

            const wellsForExport = this.treatment.wellsByIndex.map((well, index) => {
                const {
                    esId,
                    slideId,
                    status,
                    kidRank,
                    isBlast,
                    inferenceData,
                    clinicGardner,
                    pgta
                } = well

                const { kidScore, blastScore } = inferenceData
                const blastScoreIndicator = isBlast
                    ? 'YES'
                    : UIUtils.formatScore(blastScore) || this.$translate('na')

                const wellRow = {
                    [fieldsMap.e_id]: index + 1,
                    [fieldsMap.well]: esId,
                    [fieldsMap.inst_slide]: this.getInstSlide(slideId),
                    [fieldsMap.status]: this.$translate(status),
                    [fieldsMap.rank]: kidRank,
                    [fieldsMap.oocyteAge]: Utils.monthsToYears(this.treatment.oocyteAge),
                    [fieldsMap.patientAge]: Utils.monthsToYears(this.treatment.patientAge),
                    [fieldsMap.implantation]:
                        UIUtils.formatScore(kidScore) || this.$translate('na'),
                    [fieldsMap.blast]: blastScoreIndicator,
                    [fieldsMap.pnCount]: this.getPnText(well),
                    [fieldsMap.abnor]: this.getHazards(well).join(' '),
                    [fieldsMap.pgta]: this.$translate(pgta),
                    [fieldsMap.gardner]: this.getClinicGardnerLabel(clinicGardner)
                }

                if (this.currentView === TREATMENT_PAGE_VIEWS.PERFORMANCE_TABLE) {
                    for (const mkEventSN of ALL_MK_EVENTS) {
                        const event = well.mkEventsMap[mkEventSN]

                        if (event) {
                            wellRow[fieldsMap[mkEventSN]] = UIUtils.formatHourSinceFertilization(
                                event.value
                            )
                        }
                    }
                } else if (this.currentView === TREATMENT_PAGE_VIEWS.ISTANBUL_TABLE) {
                    for (let i = 0; i < ALL_ISTANBUL_EVENTS.length; i++) {
                        const event = ALL_ISTANBUL_EVENTS[i]
                        if (event) {
                            wellRow[fieldsMap[event.label]] = Utils.mapIstanbulEvent(
                                well.mkEventsMap,
                                event,
                                this.treatment.hoursSinceFertilization
                            )
                        }
                    }
                }
                return wellRow
            })

            wellsForExport.sort((a, b) => a[fieldsMap.rank] - b[fieldsMap.rank])

            Utils.exportAsCSV(wellsForExport, fields, this.getFileName())
        },
        getInstSlide(slideId) {
            const { slides, instruments } = this.treatment

            const slide = slides.find(_slide => _slide._id === slideId)

            const { instrumentId } = slide
            const instrument = instruments.find(instr => instr._id === instrumentId)

            return `I${instrument.esId}/${slide.esId}`
        },
        getFileName() {
            const {
                esId,
                startDate,
                slides,
                wellsByIndex,
                hoursSinceFertilization: time
            } = this.treatment

            const { fullName, identifier1, age } = this.patient
            const name = fullName.replace(/ /g, '_')
            const patientInfo = `name-${name}__id-${identifier1}__age-${age || 'na'}`

            const date = UIUtils.formatDate(startDate, false, false, 'DD-MM-YYYY')
            const slidesNum = slides.length
            const wellsNum = wellsByIndex.length
            const treatmentInfo = `treatment-${esId}__${date}__${slidesNum}slides__${wellsNum}wells__timeSinceFertilization-${time}`

            return `${patientInfo}__${treatmentInfo}`
        }
    }
}
</script>
